import { Button, Kind } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import {
  toLiveUpdatesDisableErrorKind,
  useDisableLiveUpdatesMutation,
  useGetLiveUpdatesConfigurationsQuery,
} from 'api/liveUpdates'
import type {
  LiveUpdatesContentField,
  LiveUpdatesStatusKey,
} from 'api/liveUpdates.types'
import LiveUpdatesDisabledBanner from 'components/LiveUpdatesDisabledBanner'
import Loading from 'components/Loading'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import {
  OLO_WEBHOOK_SETUP_PARAM,
  OLO_WEBHOOK_SETUP_VALUE,
} from 'constants/liveUpdates'
import palette from 'constants/palette'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { useLiveUpdatesAccess } from 'hooks/useLiveUpdatesAccess'
import useLiveUpdatesDevToolsEnabled from 'hooks/useLiveUpdatesDevToolsEnabled'
import { useLiveUpdatesState } from 'hooks/useLiveUpdatesState'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Tab from 'react-bootstrap-five/Tab'
import { useDispatch } from 'react-redux'
import { Redirect } from 'react-router-dom'
import { history } from 'store'
import { useAbility } from 'utilities/ability'
import { isNonProductionEnvironment } from 'utilities/urlUtils'
import { FLYBUY_PROVIDER_KEY, sectionForStatuses } from './components'
import ActionsMenu from './components/ActionsMenu'
import type { ActionsMenuItem } from './components/ActionsMenu'
import { pickerOptionLabel } from './components/ConfigurationPicker'
import ConfigurationTab from './components/ConfigurationTab'
import DiscardTurnOffModal from './components/DiscardTurnOffModal'
import LocationsModal from './components/LocationsModal'
import OloWebhookModal from './components/OloWebhookModal'
import OverviewTab from './components/OverviewTab'
import { STATUS_STAGE } from './components/preview'
import type { PreviewContent, TrackerStage } from './components/preview'
import PublishConfirmModal from './components/PublishConfirmModal'
import PublishFocusHandoff from './components/PublishFocusHandoff'
import { isOloProvider } from './components/trigger'
import type { OloWebhookPanel } from './components/trigger'
import TurnOffConfirmModal from './components/TurnOffConfirmModal'
import UnsavedChangesModal from './components/UnsavedChangesModal'
// These (and their own further imports -- stackPresets.ts, oloWebhookPresets.ts,
// mockFetch.ts, fixtures.ts) now ship in every build INCLUDING production, not
// just dev/staging/sandbox: unlike the old `NODE_ENV === 'development'` check
// (a literal webpack's DefinePlugin could substitute and dead-code-eliminate),
// `isNonProductionEnvironment()` is a runtime function call terser can't fold
// away. That's an accepted tradeoff of this being a runtime toggle at all --
// the whole point is reaching deployed sandbox/staging bundles, which are
// built identically to production apart from env vars, so there's no way to
// exclude this code from exactly one of the three without excluding it from
// all three. It's inert (never rendered, never installs the fetch mock)
// wherever isNonProductionEnvironment() or the ProfileMenu toggle read false.
import DevStackMock from './devTools/DevStackMock'
import DevStackToolbar from './devTools/DevStackToolbar'
import { CONTENT_MAX_WIDTH, PAGE_MAX_WIDTH } from './index.constants'
import { NAVBAR_HEIGHT, useCollapsingHeader } from './useCollapsingHeader'
import { useLiveUpdatesConfiguration } from './useLiveUpdatesConfiguration'
import { useOloWebhookConnection } from './useOloWebhookConnection'
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard'

const t = buildTranslate('live_updates')
const tErrors = buildTranslate('live_updates.errors')
const tEmpty = buildTranslate('live_updates.empty')

const PAGE_BACKGROUND = palette.grey05
// Both default to 'placed' and happen to share a reason: it's the one status
// a merchant always has something meaningful to look at (the first card on
// the page) — but they're independent settings, free to diverge if that ever
// stops being true for one but not the other.
const DEFAULT_FOCUSED_STATUS: LiveUpdatesStatusKey = 'placed'

// FR-7/FR-36/FR-37 repositioning: the page's original single-pane content
// (picker, stack chips, status sections, live preview — now ConfigurationTab)
// all moves under Configuration; Overview is the new education +
// connection-health layer, and is the default landing tab — it's the whole
// point of FR-7's education layer that a merchant sees it before diving into
// settings, not something they have to navigate to.
enum Tabs {
  OVERVIEW = 'overview',
  CONFIGURATION = 'configuration',
}

// The unsaved-changes dialog is shared by three triggers -- a blocked route
// navigation, a staged configuration switch, and a staged dev-tools-toggle
// reset -- and Discard/Publish-success both need to resolve whichever one is
// currently highest-priority the same way. Pure and parameterized (rather
// than reading component state/refs directly) so both call sites can share
// it: Discard reads live state, Publish-success reads refs (to avoid a
// stale closure after its own `await`) -- see their call sites.
type PendingTriggerResolution =
  | { kind: 'routeNav' }
  | { kind: 'switch'; configurationId: string }
  | { kind: 'devToolsReset' }
  | { kind: 'none' }

// Priority: a blocked route navigation (the most recent intent -- the
// merchant hit Back or a link) wins over a staged config switch, which wins
// over a staged dev-tools reset (the lowest-intent, background trigger).
const resolveHighestPriorityTrigger = (
  isPromptOpen: boolean,
  pendingConfigurationId: string | null,
  isDevToolsResetPending: boolean
): PendingTriggerResolution => {
  if (isPromptOpen) return { kind: 'routeNav' }
  if (pendingConfigurationId !== null) {
    return { kind: 'switch', configurationId: pendingConfigurationId }
  }
  if (isDevToolsResetPending) return { kind: 'devToolsReset' }
  return { kind: 'none' }
}

// Full Live Updates (Order Status V1) merchant page shell: eligibility/RBAC
// gating, the sticky page header + Publish flow, and the Overview /
// Configuration tabs (ConfigurationTab holds the original single-pane editor;
// OverviewTab is the new education + connection-health layer).
//
// showDevTools is computed ONCE in the LiveUpdates wrapper below and passed
// down as a prop, rather than this component calling
// useLiveUpdatesDevToolsEnabled() itself -- two independent subscriptions to
// that hook (one here, one in the wrapper) would each register their own
// `window.addEventListener('storage', ...)`, and React 16 does not batch
// state updates triggered from a native (non-React) event listener. A
// 'storage' event from another tab could then trigger two SEPARATE render
// passes instead of one, in child-before-parent effect order -- meaning this
// scene's refetch effect could fire before the wrapper re-renders and mounts
// DevStackMock, hitting the real API instead of the fetch mock. A single
// shared value threaded down as a prop means there's only one subscription,
// one render pass, and JSX sibling order (DevStackMock declared before this
// component in the wrapper) reliably governs effect order.
const LiveUpdatesScene = ({ showDevTools }: { showDevTools: boolean }) => {
  const [css] = useStyletron()
  // Eligibility + RBAC gate, shared with the nav so the two can't drift (see
  // useLiveUpdatesAccess). `requireFlag: false`: the LD flag only controls the
  // nav item's visibility — a direct navigation to this route must still work
  // (and gate on real eligibility/RBAC) even when the flag is off.
  const access = useLiveUpdatesAccess({ requireFlag: false })
  // Merchant-scoped, defaults false: assume push still works unless LD says so.
  const isPushDisabled = useFlag('order-status-push-disabled', false)
  const merchant = useCurrentMerchant()
  const liveUpdatesState = useLiveUpdatesState()
  // Local until the Publish that carries `enable_live_updates`, so a reload
  // or leaving the page drops it.
  const [isTurnOnRequested, setIsTurnOnRequested] = useState(false)
  const isTurnOnPendingPublish =
    isTurnOnRequested && liveUpdatesState.isDisabled
  const isLiveUpdatesDisabled =
    liveUpdatesState.isDisabled && !isTurnOnPendingPublish
  // A change in the server state, such as a publish taking the brand live,
  // supersedes the local turn on.
  useEffect(() => {
    setIsTurnOnRequested(false)
  }, [liveUpdatesState.isDisabled])
  // A logo-less merchant's `logo_image_urls.default` isn't null — Paperclip
  // returns its `.../missing.png` sentinel instead — so that must be treated
  // as "no logo" too (see PushRedesigned.tsx's identical guard).
  const merchantLogoUrl = merchant?.logo_image_urls?.default
  const hasMerchantLogo =
    !!merchantLogoUrl && !/\/missing\.\w+(\?|$)/.test(merchantLogoUrl)
  const appIconUrl = hasMerchantLogo ? merchantLogoUrl : undefined

  // Hold the configurations fetch until the summary confirms eligibility — an
  // ineligible merchant (or one whose summary hasn't resolved yet) should never
  // trigger the list request (FR-76). Also skip it for an eligible-but-
  // unauthorized user (the RBAC deep-link guard redirects them below): don't pull
  // configuration data into the cache for someone who can't manage it.
  const configurationsQuery = useGetLiveUpdatesConfigurationsQuery(undefined, {
    skip: !access.eligible || !access.canAccess,
  })

  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.OVERVIEW)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // A configuration switch requested while the buffer is dirty is staged here
  // until the unsaved-changes dialog resolves (discard / keep / publish).
  const [pendingConfigurationId, setPendingConfigurationId] = useState<
    string | null
  >(null)
  // Same idea, for a dev-tools-toggle-triggered reset that landed while the
  // buffer was dirty -- unlike pendingConfigurationId there's no explicit
  // target id to resolve to (Discard just nulls selectedId so the "default
  // to the first configurable row" effect re-picks from the fresh data).
  const [isDevToolsResetPending, setIsDevToolsResetPending] = useState(false)
  const [focusedStatus, setFocusedStatus] = useState<LiveUpdatesStatusKey>(
    DEFAULT_FOCUSED_STATUS
  )
  const [isLocationsOpen, setIsLocationsOpen] = useState(false)
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false)
  const [isTurnOffOpen, setIsTurnOffOpen] = useState(false)
  const [isDiscardTurnOffOpen, setIsDiscardTurnOffOpen] = useState(false)
  const [successDismissed, setSuccessDismissed] = useState(false)

  const config = useLiveUpdatesConfiguration(selectedId, isTurnOnPendingPublish)
  const dispatch = useDispatch()
  const canManageSettings = useAbility().can('manage', 'MerchantSettings')
  const [
    disableLiveUpdates,
    {
      isLoading: isTurningOff,
      isError: hasTurnOffError,
      error: turnOffError,
      reset: resetTurnOff,
    },
  ] = useDisableLiveUpdatesMutation()
  // The collapsing-header hook publishes the header's height to a CSS var so the
  // preview can pin directly beneath it and ride up smoothly as it collapses.
  const { sentinelRef, headerRef, isCollapsed } = useCollapsingHeader()
  const {
    isPromptOpen,
    proceed,
    cancel: cancelPrompt,
  } = useUnsavedChangesGuard(config.isDirty)

  // handleUnsavedPublish awaits an in-flight publish — a route navigation can
  // raise isPromptOpen (or a switch can stage pendingConfigurationId) during
  // that await, after the callback already captured its closure. `proceed` is
  // just as stale as `isPromptOpen` here: useUnsavedChangesGuard recreates it
  // whenever its internal `pending` changes, so the `proceed` this callback
  // closed over would still resolve the OLD (null) pending transition even
  // though isPromptOpen now correctly reads true from the ref below — calling
  // it would silently no-op instead of navigating. Mirror isPromptOpen +
  // proceed together (they're a matched pair) and pendingConfigurationId into
  // refs so the callback reads what's pending NOW, not what was pending when
  // it started.
  const guardRef = useRef({ isPromptOpen, proceed })
  guardRef.current = { isPromptOpen, proceed }
  const pendingConfigurationIdRef = useRef(pendingConfigurationId)
  pendingConfigurationIdRef.current = pendingConfigurationId
  const isDevToolsResetPendingRef = useRef(isDevToolsResetPending)
  isDevToolsResetPendingRef.current = isDevToolsResetPending

  // Tracks whether the full editor shell has ever rendered successfully.
  // Only the very first configuration load may fall back to the full-page
  // <Loading/> below — a later switch to a different configuration must keep
  // the header/tabs/scroll position mounted while its detail fetches
  // (Sandbox QA: swapping the whole shell for a small spinner shrinks the
  // page below the current scroll offset, and the browser clamps scroll back
  // to 0, which merchants saw as the page refreshing and jumping to the top).
  const hasLoadedShellRef = useRef(false)

  const configurations = useMemo(
    () => configurationsQuery.data?.configurations ?? [],
    [configurationsQuery.data]
  )

  // Default to the first configurable row (else the first row) once the list
  // loads. Only runs while nothing is selected yet.
  useEffect(() => {
    if (selectedId !== null || configurations.length === 0) return
    const firstConfigurable = configurations.find(
      configuration => configuration.configurable
    )
    setSelectedId(firstConfigurable?.id ?? configurations[0].id)
  }, [configurations, selectedId])

  // Flipping the dev tools toggle while this scene is already mounted doesn't
  // retroactively apply: DevStackMock only patches window.fetch on its own
  // mount effect, so a configurations fetch already sitting in the RTK Query
  // cache (real merchant data, fetched before the toggle went on) would keep
  // rendering even though the toolbar now shows -- and the reverse (stale
  // mocked data lingering after turning it back off). Refetching on every
  // change picks up whichever fetch implementation is current. Skips the
  // initial mount (the ref starts true) so this doesn't fire an extra
  // request before the user has touched the toggle at all. Read through a
  // ref (matching guardRef/pendingConfigurationIdRef above) rather than
  // depending on configurationsQuery directly, since that object is a fresh
  // reference on every render and would otherwise refire this on every
  // render too.
  const configurationsQueryRef = useRef(configurationsQuery)
  configurationsQueryRef.current = configurationsQuery
  const isInitialDevToolsRender = useRef(true)
  // Bumped (not just flipped true/false) so a SECOND toggle change fired
  // before the first refetch settles isn't silently swallowed: the settle
  // effect below only acts once per generation it hasn't already resolved,
  // so an earlier toggle's settle can't be mistaken for a later one's.
  // Belt-and-suspenders more than a fix for an observed bug: RTK Query's own
  // requestId-based dedup (see node_modules/@reduxjs/toolkit's query slice
  // reducer) discards a stale `fulfilled` action for a superseded request,
  // so isFetching only ever transitions false once, after the LAST refetch
  // settles -- a rapid double-toggle can't actually produce two independent,
  // separately-observable settle events to fake in a test without
  // misrepresenting how RTK Query really behaves. This still earns its
  // keep by filtering out an isFetching transition from something
  // UNRELATED to this toggle (e.g. a tag-invalidation refetch elsewhere)
  // from spuriously resetting selectedId.
  const devToolsRefetchGenerationRef = useRef(0)
  const settledDevToolsGenerationRef = useRef(0)
  useEffect(() => {
    if (isInitialDevToolsRender.current) {
      isInitialDevToolsRender.current = false
      return
    }
    // A skipped/uninitialized query (eligibility/RBAC hasn't resolved yet,
    // or was denied) has never started -- RTK Query's refetch() throws
    // "Cannot refetch a query that has not been started yet" in that case.
    // Uncaught, that propagates to the ROOT error boundary and crashes the
    // whole app, not just this page. Only refetch (and only expect a
    // settlement below to react to) once the query has actually run.
    if (configurationsQueryRef.current.isUninitialized) return
    devToolsRefetchGenerationRef.current += 1
    configurationsQueryRef.current.refetch()
  }, [showDevTools])

  // Resets selectedId only once the toggle-triggered refetch above has
  // actually SETTLED (isFetching back to false) for the LATEST toggle
  // change, rather than immediately when it's kicked off, and never while
  // the edit buffer is dirty. refetch() resolves asynchronously, so
  // resetting selectedId to null in that same effect raced it against the
  // "default to the first configurable row" effect above: that effect fires
  // as soon as selectedId is null, regardless of whether `configurations`
  // has caught up yet -- so it was picking from the STALE (pre-toggle) list,
  // and once selectedId is non-null again its own guard blocks it from
  // correcting once the fresh list actually arrives. A stale id then 404s
  // against whichever fetch implementation is now current (real ids and the
  // mock's preset ids live in disjoint namespaces), landing on a permanent
  // load-failed banner -- which renders before DevStackToolbar in the tree,
  // hiding the very picker that could recover from it. Comparing GENERATIONS
  // (not just a boolean) means an intermediate settle from an earlier toggle
  // can't resolve a later one if the switch is flipped again before the
  // first refetch finishes.
  useEffect(() => {
    if (configurationsQuery.isFetching) return
    if (
      settledDevToolsGenerationRef.current ===
      devToolsRefetchGenerationRef.current
    ) {
      return
    }
    settledDevToolsGenerationRef.current = devToolsRefetchGenerationRef.current
    // A dirty buffer would be silently discarded by resetting selectedId --
    // stage it behind the same unsaved-changes dialog used for an explicit
    // switch (handleSelectConfiguration) instead of applying it immediately.
    // Known narrow gap: if a route-nav block or a staged switch is ALREADY
    // occupying the dialog when this fires, isDevToolsResetPending is set
    // but the dialog doesn't reopen for it -- Discard/Keep editing/Publish
    // resolve the higher-priority trigger and drop this one without ever
    // nulling selectedId. selectedId then still points at an id that may
    // not exist in the (already-swapped) fresh list. Dev-tools-only, and
    // degrades to the recoverable load-failed banner rather than a crash or
    // data loss -- not fixed here; would need staging a queue of pending
    // triggers rather than the current three-flag model.
    if (config.isDirty) {
      setIsDevToolsResetPending(true)
      return
    }
    setSelectedId(null)
  }, [configurationsQuery.isFetching, config.isDirty])

  // Switching configurations resets page-local UI (CLAUDE.md id-prop rule); the
  // edit buffer + publish state reset inside useLiveUpdatesConfiguration.
  useEffect(() => {
    setFocusedStatus(DEFAULT_FOCUSED_STATUS)
    setIsLocationsOpen(false)
    setIsPublishConfirmOpen(false)
  }, [selectedId])

  // A fresh publish success un-dismisses the banner so it shows again.
  useEffect(() => {
    if (config.publishSucceeded) setSuccessDismissed(false)
  }, [config.publishSucceeded])

  const selectedConfiguration = useMemo(
    () =>
      configurations.find(configuration => configuration.id === selectedId) ??
      null,
    [configurations, selectedId]
  )

  // Olo is modeled as the ordering provider in this domain — the webhook
  // credential/receipts gate keys off orderingProvider only. StatusCard's
  // per-card gate resolves a different slot for the same question; both share
  // isOloProvider so only the scope differs, never the definition.
  const isOloOrdering = isOloProvider(
    selectedConfiguration?.stack.orderingProvider
  )
  // Mirrors StatusCard's per-card Flybuy gate, scoped to the whole preview
  // column: the in-app tab shows the Flybuy check-in mock only for a Flybuy
  // status-provider stack.
  const isFlybuyProvider =
    selectedConfiguration?.stack.statusProvider?.key === FLYBUY_PROVIDER_KEY
  const oloWebhookConnection = useOloWebhookConnection(isOloOrdering)

  // Assembled once here rather than per card: this is the only place that
  // knows both that the merchant is on Olo and that the queries have settled
  // (connectionState stays undefined until they do). Withholding the whole
  // object is how "no panel" is expressed downstream.
  const { connectionState, lastReceivedAt, needsReconnect, openModal } =
    oloWebhookConnection
  const oloWebhook: OloWebhookPanel | undefined = useMemo(
    () =>
      isOloOrdering && connectionState
        ? {
            connectionState,
            lastReceivedAt,
            needsReconnect,
            onManage: openModal,
          }
        : undefined,
    [isOloOrdering, connectionState, lastReceivedAt, needsReconnect, openModal]
  )

  // AI-1295 (FR-39) D1: RecoveryAI's Olo webhook banner CTA deep-links here
  // with ?olo_webhook=setup so a merchant who already clicked "Set up Olo
  // webhooks" once doesn't land on a page with the same banner and the same
  // button to click again. Reads the app's `history` singleton directly (not
  // props/context) — this scene isn't a route-matched component, and
  // react-router-dom is pinned pre-5.1 here (no useHistory/useLocation), the
  // same constraint useInboxParams.ts documents.
  //
  // Gated on BOTH isOloOrdering and a settled connectionState: useOloWebhookConnection
  // force-closes the modal while `enabled` (isOloOrdering) is false, and
  // isOloOrdering itself is false until the selected configuration loads — so
  // firing on isOloOrdering alone could race the modal closed again.
  //
  // A multi-configuration merchant whose default-selected configuration isn't
  // Olo would otherwise never satisfy this gate (the param left in the URL,
  // inert) — the effect below switches selectedId to the first Olo
  // configuration first, so isOloOrdering flips true on the next render and
  // this effect's own gate fires normally.
  useEffect(() => {
    if (!isOloOrdering || connectionState === undefined) return

    const params = new URLSearchParams(history.location.search)
    if (params.get(OLO_WEBHOOK_SETUP_PARAM) !== OLO_WEBHOOK_SETUP_VALUE) return

    openModal()
    params.delete(OLO_WEBHOOK_SETUP_PARAM)
    const search = params.toString()
    history.replace({
      pathname: history.location.pathname,
      search: search ? `?${search}` : '',
      hash: history.location.hash,
    })
  }, [isOloOrdering, connectionState, openModal])

  // AI-1295 (FR-39) review follow-up: picks the first Olo configuration for
  // the deep-link above when the currently SELECTED configuration isn't Olo,
  // so a multi-configuration merchant's setup deep-link isn't inert. Skips
  // while config.isDirty rather than staging it through the unsaved-changes
  // dialog (handleSelectConfiguration's own guard) — an edit already in
  // flight the instant this page mounts from a fresh deep-link is not a
  // realistic race, so silently leaving the param for a later render (once
  // the buffer clears) is enough here without wiring a fourth trigger into
  // that dialog's priority resolution.
  useEffect(() => {
    if (isOloOrdering || config.isDirty) return

    const params = new URLSearchParams(history.location.search)
    if (params.get(OLO_WEBHOOK_SETUP_PARAM) !== OLO_WEBHOOK_SETUP_VALUE) return

    const firstOloConfiguration = configurations.find(configuration =>
      isOloProvider(configuration.stack.orderingProvider)
    )
    if (firstOloConfiguration && firstOloConfiguration.id !== selectedId) {
      setSelectedId(firstOloConfiguration.id)
    }
  }, [isOloOrdering, config.isDirty, configurations, selectedId])

  // Delivery and cancellation statuses reach Thanx only as Olo webhooks, so
  // until those webhooks are actually delivering events (not_connected, and
  // 'waiting' — connected but nothing has ever arrived) those two sections have
  // no signal at all and swap their cards for a connect-webhooks card.
  // `oloWebhook` is already withheld unless this is an Olo configuration whose
  // queries have settled.
  const oloWebhooksGated =
    oloWebhook?.connectionState === 'not_connected' ||
    oloWebhook?.connectionState === 'waiting'

  const sectionPlans = useMemo(
    () =>
      selectedConfiguration
        ? sectionForStatuses(
            config.statuses,
            selectedConfiguration.stack,
            selectedConfiguration.deliveryConfigurable,
            oloWebhooksGated
          )
        : [],
    [config.statuses, selectedConfiguration, oloWebhooksGated]
  )

  const previewContent = useMemo<PreviewContent | null>(() => {
    const focused =
      config.statuses.find(status => status.status === focusedStatus) ??
      config.statuses[0]
    if (!focused) return null
    // Preview shows exactly what would fire: the buffered value verbatim, or the
    // API-supplied default copy when the field is empty / whitespace-only
    // (FR-41/71). Only pure-whitespace falls back to the default — a non-empty
    // value is shown as-is (NOT trimmed), so the preview never diverges from the
    // copy that publish sends (changedFields collapses only pure-whitespace to
    // the cleared sentinel; a non-empty value is published verbatim). The
    // default is empty when the backend supplies none (e.g. the preparing push,
    // which never fires, or before the companion nexus PR ships), so a blank
    // field renders nothing rather than fabricated copy.
    const shown = (field: LiveUpdatesContentField): string => {
      const buffered = field.value ?? ''
      return buffered.trim() === '' ? field.default ?? '' : buffered
    }
    return {
      statusKey: focused.status,
      // Stable stage key (never the localized label) so a copy edit can't
      // desync the active dot.
      stage: STATUS_STAGE[focused.status],
      title: shown(focused.content.statusTitle),
      pushText: shown(focused.content.pushMessageText),
      liveText: shown(focused.content.homescreenText),
      section: focused.section,
      channels: focused.channels,
    }
  }, [config.statuses, focusedStatus])

  // A tracker stage (e.g. "Preparing") can be driven by more than one status
  // (picked_up/en_route/arrived all share the "On the way" stage) — it's fully
  // disabled, and hidden as a middle track step in the preview, only once every
  // status sharing that stage is off. The Placed/Ready|Delivered endpoints
  // always render regardless (Figma: the track's anchors never disappear).
  const disabledStages = useMemo(() => {
    const enabledByStage = new Map<TrackerStage, boolean>()
    config.statuses.forEach(status => {
      const stage = STATUS_STAGE[status.status]
      enabledByStage.set(
        stage,
        (enabledByStage.get(stage) ?? false) || status.enabled
      )
    })
    const disabled = new Set<TrackerStage>()
    enabledByStage.forEach((hasEnabledStatus, stage) => {
      if (!hasEnabledStatus) disabled.add(stage)
    })
    return disabled
  }, [config.statuses])

  // Stages this configuration fires off the estimated ready time rather than a
  // provider event ('timer' is only ever offered for preparing/ready, each of
  // which maps to exactly one stage). The Overview tab's rail marks these
  // "Estimated" — they keep working whatever the integration is doing.
  const timerStages = useMemo(() => {
    const timer = new Set<TrackerStage>()
    config.statuses.forEach(status => {
      if (status.enabled && status.triggerMode === 'timer') {
        timer.add(STATUS_STAGE[status.status])
      }
    })
    return timer
  }, [config.statuses])

  const handleFocusField = useCallback((status: LiveUpdatesStatusKey) => {
    setFocusedStatus(status)
  }, [])

  const handleSelectTab = useCallback((tab: string | null) => {
    if (tab === Tabs.OVERVIEW || tab === Tabs.CONFIGURATION) {
      setActiveTab(tab)
    }
  }, [])

  // Overview's Olo webhook CTAs (the connection banner, the setup action, and
  // each stage's "Unlock with Olo" link) manage the connection from a tab
  // that can't itself change anything — switch to Configuration underneath
  // the modal (Sandbox QA) so it's already on the tab that owns webhook setup
  // once the merchant closes it. The Configuration tab's own OloWebhookBanner
  // CTA keeps opening the modal directly; it's already on that tab.
  const handleManageOloWebhookFromOverview = useCallback(() => {
    setActiveTab(Tabs.CONFIGURATION)
    openModal()
  }, [openModal])

  const handleSelectConfiguration = useCallback(
    (selected: { value: string }[]) => {
      const next = selected[0]?.value
      if (!next || next === selectedId) return
      // Switching with a dirty buffer would silently discard the edits — stage
      // the requested id and open the unsaved-changes dialog instead of
      // applying the switch immediately.
      if (config.isDirty) {
        setPendingConfigurationId(next)
        return
      }
      setSelectedId(next)
    },
    [config.isDirty, selectedId]
  )

  // The publish result (success banner or error) renders only inside the
  // Configuration tab, and Publish also runs from Overview.
  const showPublishResult = useCallback(
    () => setActiveTab(Tabs.CONFIGURATION),
    []
  )

  const handleConfirmPublish = useCallback(async () => {
    await config.publish()
    setIsPublishConfirmOpen(false)
    showPublishResult()
  }, [config, showPublishResult])

  // Edits made before the brand was turned off (here or in another tab) can
  // never be published while it is off. Drop them so the unsaved-changes guard
  // does not nag about a buffer that has no way out.
  const { discardEdits } = config
  useEffect(() => {
    if (isLiveUpdatesDisabled) discardEdits()
  }, [isLiveUpdatesDisabled, discardEdits])

  // The popover option that opened the dialog unmounts with the popover, so the
  // modal cannot return focus to it (returnFocus is off). Cancel returns to
  // the menu trigger; a successful turn off moves focus to the header Turn on
  // button that replaces the menu.
  const actionsMenuRef = useRef<HTMLDivElement>(null)
  const turnOnButtonWrapperRef = useRef<HTMLDivElement>(null)
  const pendingTurnOffFocusRef = useRef<'menu' | 'turn_on' | null>(null)

  const cancelPendingTurnOn = useCallback(() => {
    pendingTurnOffFocusRef.current = 'turn_on'
    setIsTurnOnRequested(false)
  }, [])

  const openTurnOffModal = useCallback(() => {
    resetTurnOff()
    pendingTurnOffFocusRef.current = null
    setIsTurnOffOpen(true)
  }, [resetTurnOff])

  // Turn off after a local turn on only drops the pending turn on and, with it,
  // the edits made since. Confirm first when there are edits to lose.
  const handleTurnOff = useCallback(() => {
    if (!isTurnOnPendingPublish) {
      openTurnOffModal()
    } else if (config.isDirty) {
      pendingTurnOffFocusRef.current = null
      setIsDiscardTurnOffOpen(true)
    } else {
      cancelPendingTurnOn()
    }
  }, [
    isTurnOnPendingPublish,
    config.isDirty,
    openTurnOffModal,
    cancelPendingTurnOn,
  ])

  const handleDiscardTurnOff = useCallback(() => {
    setIsDiscardTurnOffOpen(false)
    cancelPendingTurnOn()
  }, [cancelPendingTurnOn])

  const handleKeepTurnOn = useCallback(() => {
    pendingTurnOffFocusRef.current = 'menu'
    setIsDiscardTurnOffOpen(false)
  }, [])

  const handleCancelTurnOff = useCallback(() => {
    resetTurnOff()
    pendingTurnOffFocusRef.current = 'menu'
    setIsTurnOffOpen(false)
  }, [resetTurnOff])

  const handleConfirmTurnOff = useCallback(async () => {
    try {
      await disableLiveUpdates().unwrap()
    } catch {
      return
    }
    pendingTurnOffFocusRef.current = 'turn_on'
    setIsTurnOffOpen(false)
    dispatch(
      alert({
        key: 'success',
        message: t('turn_off_modal.success'),
        timeout: 5,
      })
    )
  }, [disableLiveUpdates, dispatch])

  const handleTurnOn = useCallback(() => {
    setIsTurnOnRequested(true)
    setIsFocusHandoffPending(true)
  }, [])
  const publishButtonRef = useRef<HTMLButtonElement>(null)
  const [isFocusHandoffPending, setIsFocusHandoffPending] = useState(false)
  const handleFocusHandoffDone = useCallback(
    () => setIsFocusHandoffPending(false),
    []
  )
  const turnOnHandler = canManageSettings ? handleTurnOn : undefined
  // Runs once the dialog is gone. The Turn on button may mount after the dialog
  // closes (after a turn off or a local turn on drop), so those states are deps
  // and the ref stays set until the target exists.
  useEffect(() => {
    const target = pendingTurnOffFocusRef.current
    if (isTurnOffOpen || isDiscardTurnOffOpen || !target) return
    const element =
      target === 'menu'
        ? actionsMenuRef.current?.querySelector<HTMLElement>(
            '[aria-haspopup="true"]'
          )
        : turnOnButtonWrapperRef.current?.querySelector<HTMLElement>('button')
    if (!element) return
    element.focus()
    pendingTurnOffFocusRef.current = null
  }, [
    isTurnOffOpen,
    isDiscardTurnOffOpen,
    isLiveUpdatesDisabled,
    isTurnOnPendingPublish,
  ])

  const handleReload = useCallback(() => {
    config.refetch()
    config.discardEdits()
  }, [config])

  // The unsaved-changes dialog is shared by three triggers: a blocked route
  // navigation (useUnsavedChangesGuard), a staged configuration switch, and a
  // staged dev-tools-toggle reset — more than one can be pending at once
  // (e.g. the browser Back button fires while the switch's dialog is already
  // open). Its three actions resolve every trigger currently pending, not
  // just one, or the dialog would need a second, redundant click to close.
  const isSwitchPending = pendingConfigurationId !== null
  const isUnsavedModalOpen =
    isPromptOpen || isSwitchPending || isDevToolsResetPending

  const handleUnsavedDiscard = useCallback(() => {
    config.discardEdits()
    // Resolve every trigger in one action -- settling all of them in one
    // click means no stale `pending` keeps the dialog governing the page and
    // no second, redundant click is ever needed.
    const resolution = resolveHighestPriorityTrigger(
      isPromptOpen,
      pendingConfigurationId,
      isDevToolsResetPending
    )
    setPendingConfigurationId(null)
    setIsDevToolsResetPending(false)
    switch (resolution.kind) {
      case 'routeNav':
        proceed()
        break
      case 'switch':
        setSelectedId(resolution.configurationId)
        break
      case 'devToolsReset':
        setSelectedId(null)
        break
      case 'none':
        break
    }
  }, [
    config,
    pendingConfigurationId,
    isPromptOpen,
    proceed,
    isDevToolsResetPending,
  ])

  const handleUnsavedKeepEditing = useCallback(() => {
    // Cancel every trigger in one action (a switch, a route nav, and a
    // dev-tools reset can all be pending at once) so the dialog never needs a
    // second click and no stale pending survives.
    // Known narrow gap: choosing to keep editing here does NOT undo the
    // underlying toggle-triggered refetch that already landed -- selectedId
    // stays pointed at an id that may no longer exist in the (already-
    // swapped) fresh configurations list, while the dirty edit buffer keeps
    // showing. Dev-tools-only; a full fix would mean either
    // blocking the refetch's data swap until this dialog resolves, or
    // re-running the settle check afterward -- neither attempted here to
    // avoid re-opening this same dialog in a loop (config.isDirty is still
    // true immediately after Keep editing).
    setPendingConfigurationId(null)
    setIsDevToolsResetPending(false)
    if (isPromptOpen) {
      cancelPrompt()
    }
  }, [isPromptOpen, cancelPrompt])

  const handleUnsavedPublish = useCallback(async () => {
    const succeeded = await config.publish()
    if (succeeded) {
      // Complete the staged action HERE (not via a publish-success effect) so a
      // no-op publish — which also resolves true — still settles the dialog
      // rather than leaving it hanging. Same priority resolution as Discard.
      // Read the refs (not the closed-over isPromptOpen/proceed/
      // pendingConfigurationId/isDevToolsResetPending) — a route nav or a
      // fresh switch/toggle can raise a pending trigger during the await
      // above, after this callback's own closure was already captured.
      const resolution = resolveHighestPriorityTrigger(
        guardRef.current.isPromptOpen,
        pendingConfigurationIdRef.current,
        isDevToolsResetPendingRef.current
      )
      setPendingConfigurationId(null)
      setIsDevToolsResetPending(false)
      switch (resolution.kind) {
        case 'routeNav':
          guardRef.current.proceed()
          break
        case 'switch':
          setSelectedId(resolution.configurationId)
          break
        case 'devToolsReset':
          setSelectedId(null)
          break
        case 'none':
          break
      }
      return
    }
    // Failure: close the dialog (dropping every staged action) so the page's
    // error banner is visible instead of trapped behind the modal overlay —
    // the edits stay intact for a retry.
    setPendingConfigurationId(null)
    setIsDevToolsResetPending(false)
    cancelPrompt()
    showPublishResult()
  }, [config, cancelPrompt, showPublishResult])

  // Shared load-failure surface: a negative banner + a retry button, reused by
  // the summary, configurations-list, and configuration-detail error states.
  const renderLoadFailed = (onRetry: () => void) => (
    <div
      className={css({
        padding: '48px 24px',
        maxWidth: CONTENT_MAX_WIDTH,
        margin: '0 auto',
      })}
    >
      <Notification kind="negative" size="full">
        {tErrors('load_failed')}
      </Notification>
      <Button className="mt-m" onClick={onRetry}>
        {tErrors('retry')}
      </Button>
    </div>
  )

  // --- Guard clauses (all hooks above run unconditionally) --------------------
  // Note: no flag guard here — the LD flag only hides the nav item; a direct
  // navigation to this route is gated on eligibility (FR-76) and RBAC below.

  // A summary load failure blocks the whole page — offer a retry banner rather
  // than fall through to an editor shell whose eligibility was never confirmed.
  if (access.isError) {
    return renderLoadFailed(access.refetch)
  }

  // On a hard refresh, the Merchant/MerchantUser records backing `ability`
  // load asynchronously — until they do, `access.canAccess` reflects a
  // "deny everything" fallback, not the user's real permissions. Wait for
  // identity to load before trusting the RBAC check below, otherwise a
  // refresh can intermittently redirect an authorized user to /unauthorized.
  if (access.identityLoading) {
    return <Loading />
  }

  // FR-76: ineligible merchants never reach the page. Evaluated BEFORE the RBAC
  // guard below so an ineligible merchant still goes home rather than to
  // /unauthorized — the ordering the previous `access.eligible && !canAccess`
  // form achieved by folding eligibility into the RBAC condition itself.
  if (access.summary && !access.eligible) {
    return <Redirect to="/" />
  }

  // RBAC deep-link guard: the nav renders the Live updates link DISABLED for a
  // user without the MerchantSettings permission, so a deep-link to the scene
  // must not hand them the full editor. Mirror the nav's gate (via the shared
  // access hook) and redirect, matching AlertBannerSettings' in-scene pattern.
  //
  // Deliberately NOT conditioned on `access.eligible`: useLiveUpdatesAccess now
  // skips the summary request for a user without the permission (it could only
  // 403 and raise a banner), so `summary` stays undefined and `eligible` never
  // becomes true for them. The old `eligible && !canAccess` form would never
  // fire, the FR-76 guard above needs `summary` too, and a skipped query reports
  // isLoading false — so an unauthorized deep-link would fall through to the
  // editor shell. Keep this a bare canAccess check.
  if (!access.canAccess) {
    return <Redirect to="/unauthorized" />
  }

  if (access.isLoading || configurationsQuery.isLoading) {
    return <Loading />
  }

  if (configurationsQuery.isError) {
    return renderLoadFailed(() => configurationsQuery.refetch())
  }

  // An eligible merchant with zero configurations (e.g. groups still computing
  // after onboarding, or all locations unassigned) gets an explicit empty state
  // rather than a dead page — an empty picker, no sections, no preview, and a
  // permanently disabled Publish button.
  if (configurations.length === 0) {
    return (
      <div
        className={css({
          padding: '48px 24px',
          maxWidth: CONTENT_MAX_WIDTH,
          margin: '0 auto',
        })}
      >
        <Text.Header2 tag="h1" className="m-0">
          {t('title')}
        </Text.Header2>
        <div
          className={css({
            marginTop: '16px',
            padding: '16px',
            borderRadius: '4px',
            backgroundColor: palette.grey10,
          })}
        >
          <Text.Header4 tag="h2" className="m-0">
            {tEmpty('title')}
          </Text.Header4>
          <Text.BodyText4
            color="grey70"
            className={css({ display: 'block', marginTop: '4px' })}
          >
            {tEmpty('note')}
          </Text.BodyText4>
        </div>
      </div>
    )
  }

  // The id-picking effect above hasn't run yet — one render after
  // configurations load, selectedId is still null. Falling through to the full
  // layout here would briefly render it with no preview and no sections before
  // the effect fires; showing the same loading shell instead avoids that flash.
  if (!selectedId) {
    return <Loading />
  }

  // RTK Query dispatches the id-keyed fetch from an effect, not synchronously
  // during render, so `isLoading` can still read false for one render right
  // after `selectedId` changes to a new id, before `detail` has caught up.
  // Treating a detail/selectedId mismatch as loading too closes that gap.
  const isSelectedDetailLoaded =
    !config.isLoading && !!config.detail && config.detail.id === selectedId

  // Either way this configuration's detail will never become real,
  // previewable content: a genuine fetch error (`config.isError` — RTK
  // Query keys it to the CURRENT query arg, so it can't be reporting a
  // stale, previous configuration's error), or a detail that loaded but
  // can't report a numeric version (currentVersion === null, so Publish
  // would otherwise send a fabricated base_version). Only the
  // currentVersion check is judged once loading has actually settled; a
  // mid-switch loading gap has no detail yet to judge either way.
  const isSelectedDetailUnavailable =
    config.isError ||
    (isSelectedDetailLoaded &&
      !!config.detail &&
      config.detail.currentVersion === null)

  if (
    !isSelectedDetailLoaded &&
    !isSelectedDetailUnavailable &&
    !hasLoadedShellRef.current
  ) {
    // Nothing has ever rendered yet — there is no shell to keep in place, so
    // the full-page loading screen is the only option.
    return <Loading />
  }
  // Past the first load, a switch to a not-yet-fetched configuration falls
  // through instead: `config.statuses` may be empty (nothing cached yet for
  // the new id) OR may still reflect the PREVIOUS configuration for one
  // render (the same `isLoading`-reads-false gap the comment above
  // describes) until the new detail arrives. `isDetailLoading` (passed to
  // both tabs below) tells them this gap is in progress either way, so they
  // show a scoped loading placeholder instead of rendering from data that
  // belongs to no configuration they're actually showing.

  if (isSelectedDetailUnavailable && !hasLoadedShellRef.current) {
    // First load resolved straight into an error or an unavailable detail —
    // there is no shell yet to keep in place, so the full-page retry banner
    // is the only option (mirrors the <Loading/> guard above for the same
    // reason).
    return renderLoadFailed(config.refetch)
  }
  // Past the first load, this falls through the same way the loading gap
  // does: `isDetailUnavailable` (passed to both tabs below) tells them to
  // show a scoped retry notice in place of the rail/sections/preview,
  // instead of unmounting the whole shell for the full-page banner — the
  // same scroll-jump this file exists to avoid, just via a rarer trigger
  // (or, for a fetch error, the SAME trigger this mechanism always covered,
  // just one this file used to special-case out ahead of everything else).

  // Only latched once the shell is actually about to render its real content
  // (detail loaded AND available) — a first load that resolves straight
  // into either failure state above must NOT count as "the shell has
  // rendered," or a later retry's own loading gap would silently skip the
  // full-page spinner/banner it still needs.
  if (isSelectedDetailLoaded && !isSelectedDetailUnavailable) {
    hasLoadedShellRef.current = true
  }

  // `isSelectedDetailLoaded` and `isSelectedDetailUnavailable` are NOT
  // themselves mutually exclusive — a fetch error on a never-before-fetched
  // configuration leaves `detail` undefined (so `isSelectedDetailLoaded` is
  // false) while `config.isError` is true, making both read false/true
  // independently of each other. The two booleans handed to the tabs below
  // must be exclusive, though (every consumer's doc comment says so, and
  // relies on it): `isDetailUnavailable` wins outright, and "loading" is
  // true only when NEITHER loaded NOR unavailable — not just "not loaded".
  const isDetailLoading =
    !isSelectedDetailLoaded && !isSelectedDetailUnavailable

  const isUnsupportedGroup =
    !!selectedConfiguration && !selectedConfiguration.configurable

  const canPublishWithoutEdits = isTurnOnPendingPublish && !isUnsupportedGroup

  // Also disabled while the selected detail is unavailable or still loading — publish() would otherwise silently no-op.
  const isPublishDisabled =
    isLiveUpdatesDisabled ||
    (!config.isDirty && !canPublishWithoutEdits) ||
    config.isPublishing ||
    isSelectedDetailUnavailable ||
    isDetailLoading

  const disabledBannerResume = canManageSettings
    ? { kind: 'button' as const, onClick: handleTurnOn }
    : undefined

  const menuItems: ActionsMenuItem[] = []
  if (canManageSettings && !isLiveUpdatesDisabled) {
    menuItems.push({
      key: 'turn_off',
      label: t('actions_menu.turn_off'),
      onClick: handleTurnOff,
    })
  }

  const publishButton = (
    <Button
      ref={publishButtonRef}
      disabled={isPublishDisabled}
      isLoading={config.isPublishing}
      onClick={() => setIsPublishConfirmOpen(true)}
    >
      {t('publish')}
    </Button>
  )

  return (
    <div
      className={css({
        position: 'relative',
        // Gray page (Figma) so the white header, cards, and preview read as
        // raised surfaces; fill the area below the fixed nav even when short.
        backgroundColor: PAGE_BACKGROUND,
        minHeight: `calc(100vh - ${NAVBAR_HEIGHT})`,
        // The sticky header below is in flow, so collapsing it lifts every
        // section under it by ~106px. Scroll anchoring treats that as content
        // shifting and compensates by scrolling back, which drops the page
        // under the expand threshold, which restores the height — and the
        // 200ms transition keeps feeding it, so the header oscillates instead
        // of settling (it visibly jiggles while scrolling). The collapse IS
        // the intended movement here, so opt this scene out of anchoring;
        // widening the hysteresis cannot help, as the band would have to
        // exceed the height delta, which is larger than the threshold itself.
        overflowAnchor: 'none',
      })}
    >
      {/* FR-62 scroll sentinel — once it leaves the viewport the header
          collapses to its slim form. */}
      <div ref={sentinelRef} className={css({ height: '1px' })} />

      <Tab.Container
        activeKey={activeTab}
        transition={false}
        onSelect={handleSelectTab}
      >
        {/* Full-width page header. It is sticky so the Publish button stays pinned
            near the top of the page as the merchant scrolls (FR-62); on scroll it
            collapses in place — hiding the title + subtitle so only Publish shows
            — and re-expands when scrolled back up. It pins just below the fixed
            app nav (top: NAVBAR_HEIGHT) and sits below it in the stacking order
            (z-index < the nav's 900) so it never paints over the top nav. The
            tabs live inside this same white/sticky block (below the title row,
            above the border-bottom divider) rather than on the page's grey
            background below it. */}
        <div
          ref={headerRef}
          data-testid="lu-header"
          data-collapsed={isCollapsed}
          className={css({
            position: 'sticky',
            top: NAVBAR_HEIGHT,
            zIndex: 800,
            backgroundColor: palette.white,
            borderBottom: `1px solid ${palette.grey20}`,
          })}
        >
          <div
            className={css({
              maxWidth: PAGE_MAX_WIDTH,
              margin: '0 auto',
              padding: isCollapsed ? '12px 24px' : '32px 24px',
              // Smoothly shrink the padding as it collapses.
              transition: 'padding 200ms ease',
            })}
          >
            {/* The title + subtitle stay mounted and collapse via max-height +
                opacity so the header animates smoothly (rather than snapping) as
                the merchant scrolls, leaving an empty (collapsed) row rather
                than unmounting. Publish lives in the tabs row below, always —
                see that row's own comment for why. */}
            <div
              aria-hidden={isCollapsed}
              className={css({
                overflow: 'hidden',
                maxHeight: isCollapsed ? '0px' : '120px',
                opacity: isCollapsed ? 0 : 1,
                transition: 'max-height 200ms ease, opacity 200ms ease',
              })}
            >
              <Text.Header2 tag="h1" className="m-0">
                {t('title')}
              </Text.Header2>
              <Text.BodyText3
                color="grey70"
                className={css({ display: 'block', marginTop: '8px' })}
              >
                {t('subtitle')}
              </Text.BodyText3>
            </div>
          </div>

          {/* A plain wrapper carrying the exact same max-width box as the
              header row and page body — the tab row's width tracks the
              content column directly, rather than being derived from
              TabNav's own Bootstrap `.container` padding. Publish lives here
              (inline with the tabs) always, in both the expanded and
              collapsed states — not just while collapsed — since the title
              row's own collapse is a CSS transition (200ms), and flipping
              Publish's DOM position in lockstep with the instant isCollapsed
              boolean produced a visible flash of it sitting alone while the
              title was still fading in/out. One stable home avoids that. */}
          <div
            data-testid="lu-tabs-row"
            className={css({
              maxWidth: PAGE_MAX_WIDTH,
              margin: '0 auto',
              padding: '0 24px 0 8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '24px',
            })}
          >
            {/* TabNav's `className` prop only reaches its INNER `<Nav>` element
                (see TabNav.tsx) — the outer `.border-bottom-1` div it renders
                is the actual flex item here, and it has no sizing of its own,
                so it must be wrapped rather than sized via that prop. Without
                this wrapper the Nav's content pushed the row wider than
                PAGE_MAX_WIDTH, shoving Publish off-screen to the right. */}
            <div className={css({ flex: '1 1 auto', minWidth: 0 })}>
              <TabNav
                ariaLabel={t('tabs.aria_label')}
                // Cancel TabNav's own `.container` gutter and TabItem's
                // `.nav-tabs .nav-link` inset (16px, `_tabs.scss`) so the
                // visible label lines up with the page title and content
                // body, which are both padded 24px from the wrapper above.
                // The 24px/8px split now lives on the row wrapper itself (so
                // it also insets the inline Publish button), not on TabNav.
                className={css({
                  maxWidth: 'none',
                  margin: 0,
                  padding: 0,
                })}
              >
                <TabItem state={Tabs.OVERVIEW} title={t('tabs.overview')} />
                <TabItem
                  state={Tabs.CONFIGURATION}
                  title={t('tabs.configuration')}
                />
              </TabNav>
            </div>
            <div
              className={css({
                display: 'flex',
                alignItems: 'center',
                flex: '0 0 auto',
                gap: '12px',
              })}
            >
              <div
                ref={actionsMenuRef}
                className={css({ display: 'contents' })}
              >
                <ActionsMenu items={menuItems} />
              </div>
              <PublishFocusHandoff
                isPending={isFocusHandoffPending && isTurnOnPendingPublish}
                isPublishDisabled={isPublishDisabled}
                publishRef={publishButtonRef}
                fallbackContainerRef={actionsMenuRef}
                onDone={handleFocusHandoffDone}
              />
              {canManageSettings && isLiveUpdatesDisabled && (
                <div
                  ref={turnOnButtonWrapperRef}
                  className={css({ display: 'contents' })}
                >
                  <Button kind={Kind.SECONDARY} onClick={handleTurnOn}>
                    {t('header.turn_on')}
                  </Button>
                </div>
              )}
              {publishButton}
            </div>
          </div>
        </div>

        {isLiveUpdatesDisabled && (
          <div
            // Longhand only: Baseweb styletron drops shorthand overrides.
            className={css({
              maxWidth: PAGE_MAX_WIDTH,
              marginTop: '24px',
              marginLeft: 'auto',
              marginRight: 'auto',
              paddingLeft: '24px',
              paddingRight: '24px',
            })}
          >
            <LiveUpdatesDisabledBanner
              body={t('disabled_banner.live_updates.body')}
              resumeText={t('disabled_banner.live_updates.resume')}
              resume={disabledBannerResume}
              testId="lu-disabled-banner"
            />
          </div>
        )}

        <Tab.Content>
          <Tab.Pane eventKey={Tabs.OVERVIEW}>
            {activeTab === Tabs.OVERVIEW && (
              <OverviewTab
                stack={selectedConfiguration?.stack ?? null}
                deliveryConfigurable={
                  !!selectedConfiguration?.deliveryConfigurable
                }
                isSupported={!isUnsupportedGroup}
                isLiveUpdatesDisabled={isLiveUpdatesDisabled}
                isOloOrdering={isOloOrdering}
                isFlybuyProvider={isFlybuyProvider}
                oloWebhook={oloWebhook}
                onManageOloWebhook={handleManageOloWebhookFromOverview}
                onSetUp={() => setActiveTab(Tabs.CONFIGURATION)}
                appIconUrl={appIconUrl}
                merchantName={merchant?.name}
                disabledStages={disabledStages}
                timerStages={timerStages}
                configurations={configurations}
                selectedId={selectedId}
                onSelectConfiguration={handleSelectConfiguration}
                isDetailLoading={isDetailLoading}
                isDetailUnavailable={isSelectedDetailUnavailable}
                onRetryDetail={handleReload}
                onTurnOn={turnOnHandler}
              />
            )}
          </Tab.Pane>
          <Tab.Pane eventKey={Tabs.CONFIGURATION}>
            {activeTab === Tabs.CONFIGURATION && (
              <ConfigurationTab
                isPushDisabled={isPushDisabled}
                configurations={configurations}
                selectedId={selectedId}
                onSelectConfiguration={handleSelectConfiguration}
                selectedConfiguration={selectedConfiguration}
                onViewLocations={() => setIsLocationsOpen(true)}
                oloWebhook={oloWebhook}
                onManageOloWebhook={oloWebhookConnection.openModal}
                isUnsupportedGroup={isUnsupportedGroup}
                warnings={access.summary?.warnings ?? []}
                config={config}
                successDismissed={successDismissed}
                onDismissSuccess={() => setSuccessDismissed(true)}
                onReloadConfiguration={handleReload}
                sectionPlans={sectionPlans}
                onFocusField={handleFocusField}
                previewContent={previewContent}
                appIconUrl={appIconUrl}
                disabledStages={disabledStages}
                isDetailLoading={isDetailLoading}
                isDetailUnavailable={isSelectedDetailUnavailable}
                merchantName={merchant?.name}
                isFlybuyProvider={isFlybuyProvider}
                isLiveUpdatesDisabled={isLiveUpdatesDisabled}
                onTurnOn={turnOnHandler}
              />
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {selectedConfiguration && (
        <LocationsModal
          isOpen={isLocationsOpen}
          onClose={() => setIsLocationsOpen(false)}
          configurationId={selectedConfiguration.id}
          label={selectedConfiguration.label}
          locationsCount={selectedConfiguration.locationsCount}
        />
      )}

      {isOloOrdering && (
        <OloWebhookModal
          isOpen={oloWebhookConnection.isModalOpen}
          onClose={oloWebhookConnection.closeModal}
          webhookUrl={oloWebhookConnection.webhookUrl}
          onSave={oloWebhookConnection.saveSecret}
          isSaving={oloWebhookConnection.isSaving}
          saveError={oloWebhookConnection.saveError}
        />
      )}

      <DiscardTurnOffModal
        isOpen={isDiscardTurnOffOpen}
        onDiscard={handleDiscardTurnOff}
        onCancel={handleKeepTurnOn}
      />

      <TurnOffConfirmModal
        isOpen={isTurnOffOpen}
        isTurningOff={isTurningOff}
        errorKind={
          hasTurnOffError ? toLiveUpdatesDisableErrorKind(turnOffError) : null
        }
        onConfirm={handleConfirmTurnOff}
        onCancel={handleCancelTurnOff}
      />

      <PublishConfirmModal
        isOpen={isPublishConfirmOpen}
        isPublishing={config.isPublishing}
        publishTurnsOn={isTurnOnPendingPublish}
        onPublish={handleConfirmPublish}
        onKeepEditing={() => setIsPublishConfirmOpen(false)}
      />

      <UnsavedChangesModal
        isOpen={isUnsavedModalOpen}
        isPublishing={config.isPublishing}
        publishTurnsOn={isTurnOnPendingPublish}
        onDiscard={handleUnsavedDiscard}
        onKeepEditing={handleUnsavedKeepEditing}
        onPublish={handleUnsavedPublish}
      />

      {/* Mocks this scene's endpoints across every live stack permutation and
          toggles the Olo webhook signal state (see devTools/README.md).
          Never available in production; in every other environment it still
          stays off until the ProfileMenu toggle is flipped on (off by
          default) -- see showDevTools above. */}
      {showDevTools && (
        <DevStackToolbar
          // Built here rather than memoised above, since it's only ever
          // needed behind this gate. Labelled by the real picker's own
          // helper so the toolbar and the page agree.
          stackOptions={configurations.map(configuration => ({
            value: configuration.id,
            label: pickerOptionLabel(configuration),
          }))}
          selectedStackId={selectedId}
          onSelectStack={handleSelectConfiguration}
          isSelectedStackOlo={isOloOrdering}
        />
      )}
    </div>
  )
}

// The dev mock has to be installed before the scene's first configurations
// fetch, and every loading guard inside the scene returns before its toolbar
// renders — so it mounts out here, as a sibling ABOVE the scene. Never
// production; off by default elsewhere until the ProfileMenu toggle (visible
// to any user, not just Thanx admins) is flipped on. Computed here, in the
// ONE place useLiveUpdatesDevToolsEnabled is called, and threaded down to
// LiveUpdatesScene as a prop -- see that component's own comment for why a
// second independent hook subscription there would be a real race, not just
// a theoretical one, under React 16's lack of batching for native event
// listeners. useLiveUpdatesDevToolsEnabled still runs unconditionally on
// every render (React's rules of hooks forbid gating a hook call on
// environment) -- harmless in production, since it's just a localStorage
// read + event listener setup, same as several other ungated localStorage
// reads already in this codebase.
const LiveUpdates = () => {
  const [devToolsEnabled] = useLiveUpdatesDevToolsEnabled()
  const showDevTools = isNonProductionEnvironment() && devToolsEnabled
  return (
    <>
      {showDevTools && <DevStackMock />}
      <LiveUpdatesScene showDevTools={showDevTools} />
    </>
  )
}

export default LiveUpdates
